import {valueOrDefault} from 'chart.js/helpers'
import {generateMinMaxOnZoom} from './helper'
const chartStates = new WeakMap()

function getState(chart) {
  let state = chartStates.get(chart)
  if (!state) {
    state = {
      originalScaleLimits: {},
      updatedScaleLimits: {},
      handlers: {},
      panDelta: {}
    }
    chartStates.set(chart, state)
  }
  return state
}

export function wheel(chart, event, range, limits, adjust = false) {
  const zoomSpeed = chart.zoomSpeed
  const rect = event.target.getBoundingClientRect()
  const speed = 1 + (event.deltaY >= 0 ? -zoomSpeed : zoomSpeed)
  let newY = event.clientY
  const center = getCenter(chart)
  if (adjust) {
    if (event.clientY - rect.top < center.y) {
      newY = event.clientY - 0.8 * getCenter(chart).y
    } else {
      newY = event.clientY + 0.9 * getCenter(chart).y
    }
  }

  const amount = {
    x: speed,
    y: speed,
    focalPoint: {
      x: event.clientX - rect.left,
      y: Number.parseInt(newY) - rect.top
    }
  }
  return zoom(chart, amount, range, limits)
}

export function zoom(chart, amount, range, limits, adjust = false) {
  const center = getCenter(chart)

  const {y = 1, focalPoint = adjust ? {x: center.x, y: center.y - 0.8 * center.y} : center} =
    typeof amount === 'number' ? {x: amount, y: amount} : amount
  return zoomNumericalScale(chart, y, focalPoint, range, limits) // zoomNumericalScale(chart.scales.y, y, focalPoint,limits)
}

// to use for button
function getCenter(chart) {
  const ca = chart.chartArea
  return {
    x: (ca.left + ca.right) / 2,
    y: (ca.top + ca.bottom) / 2
  }
}

function zoomNumericalScale(chart, zoom, center, range, limits) {
  const delta = zoomDelta(chart.scale.y, zoom, center)
  const newRange = {min: range.min + delta.min, max: range.max - delta.max}
  return updateRange(chart.scale.y, newRange, limits, true)
}

function zoomDelta(scale, zoom, center) {
  const range = scale.max - scale.min
  const newRange = range * (zoom - 1)

  const centerPoint = center.y
  // `scale.getValueForPixel()` can return a value less than the `scale.min` or
  // greater than `scale.max` when `centerPoint` is outside chartArea.
  const minPercent = Math.max(0, Math.min(1, (scale.getValueForPixel(centerPoint) - scale.min) / range || 0))

  const maxPercent = 1 - minPercent

  return {
    min: newRange * minPercent,
    max: newRange * maxPercent
  }
}
function getLimit(state, scale, scaleLimits, prop, fallback) {
  const limit = scaleLimits[prop]

  return valueOrDefault(limit, fallback)
}

function updateRange(scale, {min, max}, limits, zoom = false) {
  const state = getState(scale.chart)
  const {id, axis} = scale

  const scaleLimits = (limits && (limits[id] || limits[axis])) || {}
  const {minRange = 0} = scaleLimits

  const minLimit = getLimit(state, scale, scaleLimits, 'min', -Infinity)
  const maxLimit = getLimit(state, scale, scaleLimits, 'max', Infinity)

  const range = zoom ? Math.max(max - min, minRange) : scale.max - scale.min
  const offset = (range - max + min) / 2
  min -= offset
  max += offset

  if (min < minLimit) {
    min = minLimit
    max = Math.min(minLimit + range, maxLimit)
  } else if (max > maxLimit) {
    max = maxLimit
    min = Math.max(maxLimit - range, minLimit)
  }
  return {isChanged: scale.parse(min) !== scale.min || scale.parse(max) !== scale.max, value: {min, max}}
}
function pinchAxes(p0, p1) {
  // fingers position difference
  const pinchX = Math.abs(p0.clientX - p1.clientX)
  const pinchY = Math.abs(p0.clientY - p1.clientY)

  // diagonal fingers will change both (xy) axes
  const p = pinchX / pinchY
  let x, y
  if (p > 0.3 && p < 1.7) {
    x = y = true
  } else if (pinchX > pinchY) {
    x = true
  } else {
    y = true
  }
  return {x, y}
}

export function handlePinch(chart, event, range, limits, pinchState) {
  const {center, pointers} = event
  const zoomPercent = (1 / pinchState.current) * event.scale
  const rect = event.target.getBoundingClientRect()
  const pinch = pinchAxes(pointers[0], pointers[1])
  const amount = {
    x: pinch.x ? zoomPercent : 1,
    y: pinch.y ? zoomPercent : 1,
    focalPoint: {
      x: center.x - rect.left,
      y: center.y - rect.top
    }
  }
  return zoom(chart, amount, range, limits)
}

export const handleZoomOnWheel = (e, zoomLimits, chartRef, setZoomProps) => {
  if (e.cancelable) {
    e.preventDefault()
  }
  const range = {max: chartRef.current?.scales.y.max, min: chartRef.current?.scales.y.min}
  const chart = {
    scale: chartRef.current?.scales,
    chartArea: chartRef.current?.chartArea,
    zoomSpeed: 0.6
  }
  const {value} = wheel(chart, e, range, zoomLimits)
  let newValues = generateMinMaxOnZoom(value.min, value.max, zoomLimits.y.min, zoomLimits.y.max)
  if (newValues.max === range.max && newValues.min === range.min && newValues.max - newValues.min > 1000) {
    const {value} = wheel(chart, e, range, zoomLimits, true)
    newValues = generateMinMaxOnZoom(value.min, value.max, zoomLimits.y.min, zoomLimits.y.max)
  }

  chartRef.current.zoomScale('y', newValues, 'none')
  chartRef.current.scales.y.options.ticks.stepSize = newValues.diff
  chartRef.current.update()
  if (chartRef.current?.getZoomLevel() > 1) {
    setZoomProps((disableZoom) => ({
      ...disableZoom,
      out: false,
      reset: false,
      in: newValues.max - newValues.min <= 1000
    }))
  } else {
    setZoomProps((disableZoom) => ({...disableZoom, reset: true, out: true}))
  }
}

export const handleResetZoom = (chartRef, stepCount) => {
  if (chartRef.current) {
    chartRef.current.resetZoom()

    chartRef.current.scales.y.options.ticks.stepSize = stepCount
    chartRef.current.update()
  }
}
export const handleZoomOut = (chartRef, zoomLimits, setZoomProps) => {
  const range = {max: chartRef.current?.scales.y.max, min: chartRef.current?.scales.y.min}
  const chart = {
    scale: chartRef.current?.scales,
    chartArea: chartRef.current?.chartArea
  }
  if (chartRef.current) {
    const {value} = zoom(chart, 0.7, range, zoomLimits)
    const newValues = generateMinMaxOnZoom(value.min, value.max, zoomLimits.y.min, zoomLimits.y.max)
    chartRef.current.zoomScale('y', newValues, 'none')
    chartRef.current.scales.y.options.ticks.stepSize = newValues.diff
    chartRef.current.update()
  }
  if (chartRef.current?.getZoomLevel() === 1) {
    setZoomProps((zoomProps) => ({...zoomProps, reset: true, out: true}))
  }
}
export const handleZoomIn = (chartRef, zoomLimits, setZoomProps) => {
  const range = {max: chartRef.current?.scales.y.max, min: chartRef.current?.scales.y.min}
  const chart = {
    scale: chartRef.current?.scales,
    chartArea: chartRef.current?.chartArea
  }
  if (chartRef.current) {
    const {value} = zoom(chart, 1.4, range, zoomLimits)
    let newValues = generateMinMaxOnZoom(value.min, value.max, zoomLimits.y.min, zoomLimits.y.max)
    if (newValues.max === range.max && newValues.min === range.min && newValues.max - newValues.min > 1000) {
      const {value} = zoom(chart, 1.6, range, zoomLimits, true)
      newValues = generateMinMaxOnZoom(value.min, value.max, zoomLimits.y.min, zoomLimits.y.max)
    }

    chartRef.current.zoomScale('y', newValues, 'none')
    chartRef.current.scales.y.options.ticks.stepSize = newValues.diff
    chartRef.current.update()
    if (chartRef.current?.getZoomLevel() > 1) {
      setZoomProps((zoomProps) => ({...zoomProps, out: false, reset: false, in: newValues.max - newValues.min <= 1000}))
    }
  }
}

export const handleZoomOnPinch = (e, zoomLimits, pinchState, chartRef, setZoomProps) => {
  const range = {max: chartRef.current?.scales.y.max, min: chartRef.current?.scales.y.min}
  const chart = {
    scale: chartRef.current?.scales,
    chartArea: chartRef.current?.chartArea,
    zoomSpeed: 0.3
  }
  const {value} = handlePinch(chart, e, range, zoomLimits, pinchState)
  const newValues = generateMinMaxOnZoom(value.min, value.max, zoomLimits.y.min, zoomLimits.y.max)
  chartRef.current.zoomScale('y', newValues, 'none')
  chartRef.current.scales.y.options.ticks.stepSize = newValues.diff
  chartRef.current.update()
  if (chartRef.current?.getZoomLevel() > 1) {
    setZoomProps((disableZoom) => ({...disableZoom, out: false, reset: false}))
  } else {
    setZoomProps((disableZoom) => ({...disableZoom, reset: true, out: true}))
  }
}
